import { useState, useMemo, memo, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { TransactionLogEntry } from './TransactionLogEntry'; 
import { useVirtualizer } from '@tanstack/react-virtual';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { usernameCacheService } from '../services/usernameCache';
import { RedisKeyInfo } from './RedisKeyInfo';

interface ExpandedLogModalProps {
  title: string;
  logs: any[];
  clusterId: string;
  onClose: () => void;
  searchKeys: string[];
  redisKey?: string | null;
}

const LogItem = memo(({ entry, clusterId }: { entry: any, clusterId: string }) => {
    if (entry.msg) { // It's a standard log entry
        return (
            <div className="border-b border-glass-200/10 py-2">
                <div className="flex justify-between items-center gap-2 text-xs text-glass-400 mb-1 tabular-nums">
                    <span>{new Date(parseInt(entry.id.split('-')[0])).toLocaleString()}</span>
                    <span className="badge badge-info font-mono">{entry.msg.action}</span>
                </div>
                <TransactionLogEntry entry={entry.msg} clusterId={clusterId} />
            </div>
        );
    }
    return null; 
});

export const ExpandedLogModal = ({ title, logs, clusterId, onClose, searchKeys, redisKey }: ExpandedLogModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredLogs = useMemo(() => {
    if (!searchQuery) return logs;
    const lowerCaseQuery = searchQuery.toLowerCase();

    return logs.filter(entry => {
      // Create a string of all searchable content for the entry
      const searchableContent = searchKeys.map(key => {
        const field = key.substring(4); // remove "msg."
        const value = entry.msg?.[field];

        if (['user', 'from', 'to'].includes(field) && typeof value === 'string') {
          const username = usernameCacheService.getName(value);
          return [value, username].filter(Boolean).join(' ');
        }
        return value;
      }).join(' ').toLowerCase();

      return searchableContent.includes(lowerCaseQuery);
    });
  }, [searchQuery, logs, searchKeys]);

  const parentRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const rowVirtualizer = useVirtualizer({
    count: filteredLogs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
    overscan: 5,
  });

  return (
    <div className="modal-overlay z-50">
      <div ref={modalRef} className="modal-panel max-w-4xl h-[90vh] p-4 md:p-6">
        <div className="modal-header flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <h2 className="modal-title truncate">{title}</h2>
            <span className="badge badge-neutral">{filteredLogs.length.toLocaleString()}</span>
            <RedisKeyInfo redisKey={redisKey} />
          </div>
          <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="relative mb-4 flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-9"
          />
        </div>
        <div ref={parentRef} className="modal-body pr-2">
            <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
                {rowVirtualizer.getVirtualItems().map(virtualItem => {
                    const item = filteredLogs[virtualItem.index];
                    return (
                        <div
                            key={virtualItem.key}
                            ref={rowVirtualizer.measureElement}
                            data-index={virtualItem.index}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            <LogItem entry={item} clusterId={clusterId} />
                        </div>
                    );
                })}
            </div>
        </div>
      </div>
    </div>
  );
};
